import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { ApiError } from '@/lib/api-client';
import { safeRedirectTarget } from '@/lib/safe-redirect';
import { useAuth } from '@/app/auth-context';

interface LocationState {
  from?: string;
}

export function LoginPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signIn, verifyMfa } = useAuth();
  const [submitError, setSubmitError] = useState<string | null>(null);
  /** USR-006 — set when /auth/login returned mfa_required. */
  const [mfaToken, setMfaToken] = useState<string | null>(null);

  const schema = z.object({
    email: z.string().min(1, t('login.errors.required')).email(t('login.errors.invalidEmail')),
    password: z.string().min(1, t('login.errors.required')).min(8, t('login.errors.minPassword')),
  });
  type LoginFields = z.infer<typeof schema>;

  const form = useForm<LoginFields>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });
  const {
    register,
    formState: { errors, isSubmitting },
  } = form;

  /* If the user is already logged in, bounce them to the dashboard. */
  useEffect(() => {
    if (user) {
      const target = safeRedirectTarget((location.state as LocationState | null)?.from);
      navigate(target, { replace: true });
    }
  }, [user, location.state, navigate]);

  const handleApiError = (cause: unknown): void => {
    if (cause instanceof ApiError) {
      if (cause.status === 0) {
        setSubmitError(t('login.errors.networkError'));
      } else if (cause.status === 401) {
        setSubmitError(t('login.errors.invalidCredentials'));
      } else if (cause.status === 429) {
        setSubmitError(t('login.errors.lockedOut'));
      } else {
        setSubmitError(cause.envelope.message);
      }
    } else {
      setSubmitError(t('login.errors.networkError'));
    }
  };

  const onSubmit = async (data: LoginFields): Promise<void> => {
    setSubmitError(null);
    try {
      const result = await signIn(data.email, data.password);
      if (result.kind === 'mfa_required') {
        setMfaToken(result.mfaToken);
        return;
      }
      const target = safeRedirectTarget((location.state as LocationState | null)?.from);
      navigate(target, { replace: true });
    } catch (cause) {
      handleApiError(cause);
    }
  };

  if (mfaToken) {
    return (
      <MfaVerifySection
        mfaToken={mfaToken}
        onVerify={async (code) => {
          setSubmitError(null);
          try {
            await verifyMfa(mfaToken, code);
            const target = safeRedirectTarget((location.state as LocationState | null)?.from);
            navigate(target, { replace: true });
          } catch (cause) {
            handleApiError(cause);
          }
        }}
        onCancel={() => {
          setMfaToken(null);
          setSubmitError(null);
        }}
        submitError={submitError}
      />
    );
  }

  return (
    <section className="max-w-md mx-auto px-s5 py-s8">
      <header className="mb-s6">
        <h1 className="font-display text-display-lg text-ink">{t('login.title')}</h1>
        <p className="mt-s3 text-gray-2">{t('login.subtitle')}</p>
      </header>

      <form
        onSubmit={(event) => {
          void form.handleSubmit(onSubmit)(event);
        }}
        noValidate
        className="space-y-s4"
      >
        <div>
          <label htmlFor="email" className="form-label">
            {t('login.fields.email')}
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            placeholder={t('login.placeholders.email')}
            className={`input-field ${errors.email ? 'input-error' : ''}`}
            {...register('email')}
          />
          {errors.email?.message && <p className="form-error">{errors.email.message}</p>}
        </div>

        <div>
          <label htmlFor="password" className="form-label">
            {t('login.fields.password')}
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            placeholder={t('login.placeholders.password')}
            className={`input-field ${errors.password ? 'input-error' : ''}`}
            {...register('password')}
          />
          {errors.password?.message && <p className="form-error">{errors.password.message}</p>}
        </div>

        {submitError && (
          <p role="alert" className="form-error" data-testid="submit-error">
            {submitError}
          </p>
        )}

        <button type="submit" className="btn-primary w-full" disabled={isSubmitting}>
          {isSubmitting ? t('login.submitting') : t('login.submit')}
        </button>

        <div className="text-center">
          <Link to="/forgot-password" className="text-sm text-flame-ember hover:underline">
            {t('login.forgotPassword')}
          </Link>
        </div>
      </form>
    </section>
  );
}

interface MfaVerifySectionProps {
  mfaToken: string;
  onVerify: (code: string) => Promise<void>;
  onCancel: () => void;
  submitError: string | null;
}

function MfaVerifySection({
  mfaToken: _mfaToken,
  onVerify,
  onCancel,
  submitError,
}: MfaVerifySectionProps): JSX.Element {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);

  return (
    <section className="max-w-md mx-auto px-s5 py-s8" data-testid="mfa-step">
      <header className="mb-s6">
        <h1 className="font-display text-display-lg text-ink">{t('login.mfa.title')}</h1>
        <p className="mt-s3 text-gray-2">{t('login.mfa.subtitle')}</p>
      </header>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (busy) return;
          setBusy(true);
          void onVerify(code.trim()).finally(() => {
            setBusy(false);
          });
        }}
        noValidate
        className="space-y-s4"
      >
        <div>
          <label htmlFor="mfa-code" className="form-label">
            {t('login.mfa.codeLabel')}
          </label>
          <input
            id="mfa-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder={t('login.mfa.codePlaceholder')}
            className="input-field"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
            }}
            data-testid="mfa-code-input"
          />
          <p className="form-help mt-s2 text-sm text-gray-2">{t('login.mfa.recoveryHint')}</p>
        </div>

        {submitError && (
          <p role="alert" className="form-error" data-testid="submit-error">
            {submitError}
          </p>
        )}

        <button
          type="submit"
          className="btn-primary w-full"
          disabled={busy || code.trim().length < 6}
          data-testid="mfa-submit"
        >
          {busy ? t('login.mfa.verifying') : t('login.mfa.verify')}
        </button>

        <button
          type="button"
          className="btn-secondary w-full"
          onClick={onCancel}
          disabled={busy}
        >
          {t('login.mfa.cancel')}
        </button>
      </form>
    </section>
  );
}
